import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Heart, Phone, ChevronDown, ChevronRight, LogIn, UserPlus, Building2, User, Globe, CalendarDays, Wallet, Armchair, CloudSun, ClipboardList } from "lucide-react";
import { useCategories } from "../context/CategoriesContext";
import { useMember } from "../context/MemberContext";
import { MemberBell } from "./MemberBell";
import { useAuth } from "../context/AuthContext";
import FirmaLoginDialog from "./FirmaLoginDialog";
import MemberDialog from "./MemberDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "./ui/sheet";

const mekanCatsFilter = (cats) => cats.filter((c) => c.group === "mekan");
const firmaCatsFilter = (cats) => cats.filter((c) => c.group === "firma");

export const ARAC_TOOLS = [
  { key: "davetli-listem", label: "Davetli Listem", icon: ClipboardList, desc: "Davetlilerinizi, katılım durumlarını ve kişi sayılarını takip edin." },
  { key: "web-sitem", label: "Dijital Davetiyem", icon: Globe, desc: "Düğününüze özel davetiye web sitesi oluşturun." },
  { key: "ajandam", label: "Ajandam", icon: CalendarDays, desc: "Düğün hazırlık takviminizi ve yapılacaklar listenizi yönetin." },
  { key: "butcem", label: "Bütçem", icon: Wallet, desc: "Düğün harcamalarınızı planlayın ve takip edin." },
  { key: "oturma-plani", label: "Oturma Planı", icon: Armchair, desc: "Masaları ve misafir yerleşimini kolayca düzenleyin." },
  { key: "favorilerim", label: "Favorilerim", icon: Heart, desc: "Favori olarak işaretlediğiniz mekan ve firmalar." },
  { key: "hava-durumu", label: "Hava Durumu", icon: CloudSun, desc: "Düğün gününüz için hava durumu tahminini görün." },
];

const Header = () => {
  const { categories } = useCategories();
  const mekanCats = mekanCatsFilter(categories);
  const firmaCats = firmaCatsFilter(categories);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [memberOpen, setMemberOpen] = useState(false);
  const { member } = useMember();
  const { firm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    if (!isHome) { setScrolled(true); return; }
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const go = (slug) => {
    setOpen(false);
    navigate(`/kategori/${slug}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_6px_30px_-12px_rgba(158,42,59,0.25)] py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" onClick={(e) => { e.preventDefault(); navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex items-center gap-2 group">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] shadow-md group-hover:scale-105 transition-transform">
            <Heart className="w-5 h-5 text-white" fill="white" />
          </div>
          <div className="leading-none">
            <span
              className={`font-display text-xl font-bold block ${
                scrolled ? "text-[#9E2A3B]" : "text-white text-shadow-soft"
              }`}
            >
              DüğünSenin
            </span>
            <span
              className={`text-[10px] tracking-[0.22em] ${
                scrolled ? "text-[#C6A15B]" : "text-[#E3CE9E]"
              }`}
            >
              .COM
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7 whitespace-nowrap">
          <Link
            to="/"
            className={`text-sm font-medium link-underline ${
              scrolled ? "text-[#2A2224]" : "text-white"
            }`}
          >
            Anasayfa
          </Link>

          {[
            { label: "Düğün Mekanları", cats: mekanCats },
            { label: "Düğün Firmaları", cats: firmaCats },
          ].map((m) => (
            <DropdownMenu key={m.label}>
              <DropdownMenuTrigger
                className={`flex items-center gap-1 text-sm font-medium outline-none ${
                  scrolled ? "text-[#2A2224]" : "text-white"
                }`}
              >
                {m.label}
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-64 max-h-[70vh] overflow-y-auto bg-white border-[#F6E4E6]"
              >
                {m.cats.map((c) => (
                  <DropdownMenuItem
                    key={c.slug}
                    onClick={() => go(c.slug)}
                    className="cursor-pointer text-[#2A2224] focus:bg-[#F6E4E6] focus:text-[#9E2A3B]"
                  >
                    {c.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ))}

          <Link
            to="/kategori/organizasyon-firmalari"
            className={`text-sm font-medium link-underline ${
              scrolled ? "text-[#2A2224]" : "text-white"
            }`}
          >
            Organizasyon
          </Link>

          <div className="relative group" data-testid="araclar-menu">
            <button className={`flex items-center gap-1 text-sm font-medium link-underline ${scrolled ? "text-[#2A2224]" : "text-white"}`}>
              Araçlar <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
            </button>
            {/* Altına açılan geniş yatay panel (hover) */}
            <div className="absolute top-full right-0 pt-4 hidden group-hover:block z-50">
              <div className="flex items-stretch bg-white border border-[#F6E4E6] rounded-2xl shadow-[0_25px_60px_-20px_rgba(158,42,59,0.4)] px-4 py-6 animate-in fade-in slide-in-from-top-2 duration-200">
                {ARAC_TOOLS.map((t) => {
                  const I = t.icon;
                  return (
                    <button
                      key={t.key}
                      onClick={() => navigate(`/araclar?arac=${t.key}`)}
                      data-testid={`arac-item-${t.key}`}
                      className="group/item flex flex-col items-center justify-start gap-3 px-6 py-2 w-32 text-center border-r border-[#F6E4E6] last:border-r-0 transition-colors"
                    >
                      <span className="w-12 h-12 rounded-full flex items-center justify-center bg-[#FBF5F0] text-[#C15B6B] group-hover/item:bg-[#9E2A3B] group-hover/item:text-white transition-colors">
                        <I className="w-5 h-5" />
                      </span>
                      <span className="text-sm font-medium text-[#2A2224] group-hover/item:text-[#9E2A3B] transition-colors leading-tight">
                        {t.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <Link
            to="/firma-kayit"
            className={`text-sm font-medium link-underline ${
              scrolled ? "text-[#9E2A3B]" : "text-[#E3CE9E]"
            }`}
          >
            İşletmeni Ekle
          </Link>
        </nav>

        <div className="flex items-center gap-3 whitespace-nowrap">
          <button
            onClick={() => (firm ? navigate("/firma-panel") : setLoginOpen(true))}
            data-testid={firm ? "header-firm-panel-link" : "header-firm-login-btn"}
            title={firm ? "Firma Paneline Git" : undefined}
            className={`hidden md:flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-all hover:-translate-y-0.5 max-w-[220px] ${
              scrolled
                ? "border-[#9E2A3B] text-[#9E2A3B] hover:bg-[#9E2A3B] hover:text-white"
                : "border-white/70 text-white hover:bg-white hover:text-[#9E2A3B]"
            }`}
          >
            {firm ? <Building2 className="w-4 h-4 shrink-0" /> : <LogIn className="w-4 h-4" />}
            <span className="truncate">{firm ? firm.name : "Firma Girişi"}</span>
          </button>
          {member && <MemberBell light={!scrolled} />}
          {member ? (
            <Link
              to="/uyelik"
              data-testid="member-panel-link"
              className="hidden md:flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <User className="w-4 h-4" />
              {member.name?.split(" ")[0] || "Hesabım"}
            </Link>
          ) : (
            <button
              onClick={() => setMemberOpen(true)}
              data-testid="member-open-button"
              className="hidden md:flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <UserPlus className="w-4 h-4" />
              Üye Ol / Üye Girişi
            </button>
          )}

          {/* Mobile menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className={`lg:hidden p-2 rounded-lg ${
                  scrolled ? "text-[#9E2A3B]" : "text-white"
                }`}
                aria-label="Menü"
              >
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] sm:w-96 bg-[#FBF5F0] overflow-y-auto p-0">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-8">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B]">
                    <Heart className="w-4 h-4 text-white" fill="white" />
                  </div>
                  <span className="font-display text-lg font-bold text-[#9E2A3B]">DüğünSenin</span>
                </div>

                <Link to="/" onClick={() => setOpen(false)} className="block py-3 text-[#2A2224] font-medium border-b border-[#F0DDE0]">Anasayfa</Link>

                <p className="mt-5 mb-2 text-xs font-semibold tracking-widest text-[#C6A15B] uppercase">Düğün Mekanları</p>
                <div className="grid grid-cols-1">
                  {mekanCats.map((c) => (
                    <button key={c.slug} onClick={() => go(c.slug)} className="text-left py-2 text-sm text-[#5b4d4f] hover:text-[#9E2A3B] transition-colors">{c.name}</button>
                  ))}
                </div>

                <p className="mt-5 mb-2 text-xs font-semibold tracking-widest text-[#C6A15B] uppercase">Düğün Firmaları</p>
                <div className="grid grid-cols-1">
                  {firmaCats.map((c) => (
                    <button key={c.slug} onClick={() => go(c.slug)} className="text-left py-2 text-sm text-[#5b4d4f] hover:text-[#9E2A3B] transition-colors">{c.name}</button>
                  ))}
                </div>

                <button onClick={() => { setOpen(false); firm ? navigate("/firma-panel") : setLoginOpen(true); }} data-testid="mobile-firm-btn" className="mt-6 flex w-full items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium">
                  {firm ? <Building2 className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
                  <span className="truncate">{firm ? firm.name : "Firma Girişi"}</span>
                </button>

                {member ? (
                  <Link to="/uyelik" onClick={() => setOpen(false)} className="mt-3 flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#9E2A3B] text-[#9E2A3B] text-sm font-medium">
                    <User className="w-4 h-4" /> Hesabım
                  </Link>
                ) : (
                  <button onClick={() => { setOpen(false); setMemberOpen(true); }} className="mt-3 flex w-full items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#9E2A3B] text-[#9E2A3B] text-sm font-medium">
                    <UserPlus className="w-4 h-4" /> Üye Ol / Üye Girişi
                  </button>
                )}

                <Link to="/firma-kayit" onClick={() => setOpen(false)} className="mt-3 flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#C6A15B] text-[#9E2A3B] text-sm font-medium">
                  İşletmeni Ekle
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <FirmaLoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
      <MemberDialog open={memberOpen} onOpenChange={setMemberOpen} />
    </header>
  );
};

export default Header;
